import * as DOMPurifyModule from 'dompurify';

type DOMPurifyInstance = typeof DOMPurifyModule;

/**
 * dompurify ships a default-only ESM build (webpack/storybook) and an
 * `export =` CJS build (jest). Resolve whichever shape we were given.
 */
const domPurify: DOMPurifyInstance =
  (DOMPurifyModule as unknown as { default?: DOMPurifyInstance }).default ??
  DOMPurifyModule;

/**
 * Tags forbidden in the render-time preview: they execute script, load
 * external resources, or drive navigation/submission — none of which a
 * marketing-email preview needs. Inert presentational controls (button,
 * input, select, textarea) are intentionally absent so the preview matches
 * the sent email, which keeps them (CTAs are commonly built as `<button>`s).
 */
const FORBIDDEN_TAGS: string[] = [
  'script',
  'iframe',
  'frame',
  'object',
  'embed',
  'form',
  'base',
  'template',
  'meta',
  'link',
];

/**
 * Attributes stripped from the preview even on otherwise-allowed tags: they
 * drive form submission or navigation (e.g. `formaction` on a `<button>`) or
 * steal focus. Resource-loading attributes (`src`, `srcset`, `poster`) are
 * deliberately kept so images render in the preview exactly as they will in
 * the sent email.
 */
const FORBIDDEN_PREVIEW_ATTRIBUTES: string[] = [
  'action',
  'formaction',
  'formmethod',
  'formenctype',
  'formtarget',
  'target',
  'ping',
  'download',
  'autofocus',
];

const ACTIVE_CONTENT_SELECTOR =
  'script, iframe, frame, embed, object, base, template';

const URI_ATTRIBUTES = [
  'href',
  'src',
  'action',
  'formaction',
  'background',
  'poster',
  'data',
  'xlink:href',
];

// Control characters are intentional: browsers strip them when parsing URL
// schemes, so `java\x01script:` style obfuscation must still match.
// eslint-disable-next-line no-control-regex
const JAVASCRIPT_URL_PATTERN = /^[\s\u0000-\u001f]*javascript:/i;

/**
 * Sanitises customer-provided raw HTML for the marketing email editor.
 *
 * Two passes with different threat models:
 * - `sanitise` is the strict render-time pass (DOMPurify) for HTML about to
 *   enter the application DOM.
 * - `stripActiveContent` removes only execution vectors from the STORED HTML
 *   so ProseMirror serialisation/clipboard can never execute handlers, while
 *   preserving email-client markup (MSO conditional comments, `<style>`
 *   blocks, presentational attributes) verbatim for sending.
 */
export class HTMLSanitiser {
  /**
   * Sanitises customer-provided raw HTML for rendering inside the app.
   *
   * This is a render-time defence only. The raw HTML stored on the node (and
   * serialised into outgoing emails) is kept verbatim so that email-client
   * specific markup survives sending. Any surface that renders the HTML into
   * the application DOM must go through this method first.
   *
   * DOMPurify keeps the parts marketing emails rely on (tables, images,
   * inline `style` attributes, `<style>` blocks, presentational attributes,
   * and CTA `<button>`s) while stripping scripts, event handlers,
   * `javascript:` URLs, and form-submission/navigation attributes. Active
   * tags (`form`, `meta`, `link`, `iframe`, ...) are dropped — see
   * FORBIDDEN_TAGS.
   */
  static sanitise(html: string): string {
    return domPurify.sanitize(html, {
      FORBID_TAGS: FORBIDDEN_TAGS,
      FORBID_ATTR: FORBIDDEN_PREVIEW_ATTRIBUTES,
      USE_PROFILES: { html: true },
      // Without FORCE_BODY the parser hoists leading <style> blocks (common in
      // marketing email HTML) into <head>, and they're lost on serialisation.
      FORCE_BODY: true,
    });
  }

  /**
   * Removes content that could execute script (element handlers like onerror,
   * script/embed elements, javascript: URLs) while preserving everything else -
   * including conditional comments (MSO), <style> blocks, and presentational
   * markup that DOMPurify would strip.
   *
   * Unlike sanitise (render-time, strict), this runs on the STORED HTML so
   * that parsing it into a live document during ProseMirror serialisation
   * (getHTML, clipboard) can never execute handlers such as `<img onerror>`.
   * Parsing happens in an inert document, where no fetches or handlers fire.
   */
  static stripActiveContent(html: string): string {
    const inertDocument = document.implementation.createHTMLDocument('');
    const container = inertDocument.createElement('div');
    container.innerHTML = html;

    container
      .querySelectorAll(ACTIVE_CONTENT_SELECTOR)
      .forEach((element) => element.remove());

    const walker = inertDocument.createTreeWalker(
      container,
      NodeFilter.SHOW_ELEMENT
    );
    let current = walker.nextNode();
    while (current) {
      if (current instanceof Element) {
        HTMLSanitiser._stripActiveAttributes(current);
      }
      current = walker.nextNode();
    }
    return container.innerHTML;
  }

  private static _stripActiveAttributes(element: Element): void {
    for (const attribute of Array.from(element.attributes)) {
      const name = attribute.name.toLowerCase();
      if (name.startsWith('on')) {
        element.removeAttribute(attribute.name);
        continue;
      }
      if (
        URI_ATTRIBUTES.includes(name) &&
        JAVASCRIPT_URL_PATTERN.test(attribute.value)
      ) {
        element.removeAttribute(attribute.name);
      }
    }
  }
}
